import React, { useState } from 'react';
import { Clock, User, Eye, Edit, Plus, Trash, Download, ArrowUpDown, ChevronDown, ChevronUp, ChevronLeft, ChevronRight } from 'lucide-react';
import { AuditLogEntry, AuditAction } from '@types';
import { format } from 'date-fns';
import { trpc } from '@lib/trpc';

interface AuditLogProps {
  entityId: string;
  entityType: 'invoice' | 'supplier';
  className?: string;
}


const getActionIcon = (action: AuditAction) => {
  switch (action) {
    case 'create':
      return <Plus className="w-4 h-4 text-green-500" />;
    case 'update':
      return <Edit className="w-4 h-4 text-blue-500" />;
    case 'status_change':
      return <ArrowUpDown className="w-4 h-4 text-orange-500" />;
    case 'delete':
      return <Trash className="w-4 h-4 text-red-500" />;
    case 'view':
      return <Eye className="w-4 h-4 text-gray-500" />;
    case 'export':
      return <Download className="w-4 h-4 text-purple-500" />;
    default:
      return <Clock className="w-4 h-4 text-gray-400" />;
  }
};

const getActionColor = (action: AuditAction) => {
  switch (action) {
    case 'create':
      return 'bg-green-50 border-green-200';
    case 'update':
      return 'bg-blue-50 border-blue-200';
    case 'status_change':
      return 'bg-orange-50 border-orange-200';
    case 'delete':
      return 'bg-red-50 border-red-200';
    case 'view':
      return 'bg-gray-50 border-gray-200';
    case 'export':
      return 'bg-purple-50 border-purple-200';
    default:
      return 'bg-gray-50 border-gray-200';
  }
};

const formatActionText = (log: AuditLogEntry): string => {
  switch (log.action) {
    case 'create':
      return 'Created';
    case 'update':
      if (log.changes?.fields && log.changes.fields.length > 0) {
        if (log.changes.fields.length === 1) {
          // Single field update - show before and after values
          const field = log.changes.fields[0];
          const beforeValue = log.changes.before?.[field];
          const afterValue = log.changes.after?.[field];
          
          if (beforeValue !== undefined && afterValue !== undefined) {
            return `Updated ${field}: "${beforeValue || 'empty'}" → "${afterValue || 'empty'}"`;
          }
          return `Updated ${field}`;
        } else {
          // Multiple fields - show count and list
          const fieldNames = log.changes.fields.join(', ');
          return `Updated ${log.changes.fields.length} fields: ${fieldNames}`;
        }
      }
      return 'Updated';
    case 'status_change':
      if (log.changes?.before?.status && log.changes?.after?.status) {
        return `Status changed: ${log.changes.before.status} → ${log.changes.after.status}`;
      }
      return 'Status changed';
    case 'delete':
      return 'Deleted';
    case 'view':
      return 'Viewed';
    case 'export':
      return 'Exported';
    default:
      return 'Unknown action';
  }
};

const getUserDisplayName = (log: AuditLogEntry): string => {
  if (log.user?.name) {
    return log.user.name;
  }
  if (log.user?.email) {
    return log.user.email;
  }
  if (log.user_email) {
    return log.user_email;
  }
  return 'System';
};

export const AuditLog: React.FC<AuditLogProps> = ({ entityId, entityType, className = '' }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize] = useState(10);

  // Fetch audit logs with pagination
  const { data: auditData, isLoading, error } = trpc.audit.getByEntity.useQuery({
    entityType,
    entityId,
    limit: pageSize,
    offset: currentPage * pageSize,
  });

  const auditLogs = auditData?.logs || [];
  const totalCount = auditData?.totalCount || 0;
  const totalPages = Math.ceil(totalCount / pageSize);
  const hasNextPage = currentPage < totalPages - 1;
  const hasPrevPage = currentPage > 0;

  if (isLoading) {
    return (
      <div className={`${className}`}>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-medium text-secondary-700 flex items-center">
            <Clock className="w-4 h-4 mr-2" />
            Audit Logs
          </h3>
        </div>
        <div className="text-center py-6">
          <div className="w-6 h-6 border-2 border-accent-500 border-t-transparent rounded-full animate-spin mx-auto mb-2" />
          <p className="text-sm text-secondary-500">Loading activity...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`${className}`}>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-medium text-secondary-700 flex items-center">
            <Clock className="w-4 h-4 mr-2" />
            Audit Logs
          </h3>
        </div>
        <div className="text-center py-6">
          <Clock className="w-8 h-8 text-red-300 mx-auto mb-2" />
          <p className="text-sm text-red-500">Failed to load audit logs</p>
        </div>
      </div>
    );
  }

  if (auditLogs.length === 0) {
    return (
      <div className={`${className}`}>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-medium text-secondary-700 flex items-center">
            <Clock className="w-4 h-4 mr-2" />
            Audit Logs
          </h3>
        </div>
        <div className="text-center py-6">
          <Clock className="w-8 h-8 text-secondary-300 mx-auto mb-2" />
          <p className="text-sm text-secondary-500">No activity recorded</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`${className}`}>
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-medium text-secondary-700 flex items-center">
          <Clock className="w-4 h-4 mr-2" />
          Audit Logs
          <span className="ml-2 px-2 py-1 text-xs bg-secondary-100 text-secondary-600 rounded-full">
            {totalCount}
          </span>
        </h3>
        {auditLogs.length > 0 && (
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="text-xs text-secondary-500 hover:text-secondary-700 flex items-center"
          >
            {isExpanded ? (
              <>
                <ChevronUp className="w-3 h-3 mr-1" />
                Collapse
              </>
            ) : (
              <>
                <ChevronDown className="w-3 h-3 mr-1" />
                Expand
              </>
            )}
          </button>
        )}
      </div>

      {!isExpanded ? (
        // Collapsed view - show only the most recent activity
        <div className="space-y-2">
          {auditLogs.slice(0, 1).map((log) => (
            <div
              key={log.id}
              className={`flex items-center p-3 rounded-md border ${getActionColor(log.action)}`}
            >
              <div className="flex-shrink-0 mr-3">
                {getActionIcon(log.action)}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-medium text-secondary-900 truncate">
                    {formatActionText(log)}
                  </p>
                  <span className="text-xs text-secondary-500 ml-2 flex-shrink-0">
                    {format(new Date(log.created_at), 'MMM d, HH:mm')}
                  </span>
                </div>
                <div className="flex items-center mt-1">
                  <User className="w-3 h-3 text-secondary-400 mr-1" />
                  <span className="text-xs text-secondary-600">{getUserDisplayName(log)}</span>
                </div>
              </div>
            </div>
          ))}
          {totalCount > 1 && (
            <button
              onClick={() => setIsExpanded(true)}
              className="w-full text-xs text-secondary-500 hover:text-secondary-700 py-2 flex items-center justify-center"
            >
              View {totalCount - 1} more activities
              <ChevronDown className="w-3 h-3 ml-1" />
            </button>
          )}
        </div>
      ) : (
        // Expanded view - show all activities with pagination
        <div className="space-y-3">
          {auditLogs.map((log) => (
            <div
              key={log.id}
              className={`flex p-3 rounded-md border ${getActionColor(log.action)}`}
            >
              <div className="flex-shrink-0 mr-3">
                {getActionIcon(log.action)}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-sm font-medium text-secondary-900">
                      {formatActionText(log)}
                    </p>
                    {log.action === 'update' && log.changes?.fields && log.changes.fields.length > 1 && (
                      <div className="mt-2">
                        <p className="text-xs text-secondary-600 font-medium mb-1">Changes:</p>
                        <div className="space-y-1">
                          {log.changes.fields.map(field => {
                            const beforeValue = log.changes?.before?.[field];
                            const afterValue = log.changes?.after?.[field];
                            return (
                              <div key={field} className="text-xs text-secondary-600 pl-2">
                                <span className="font-medium">{field}:</span>
                                <span className="ml-1">"</span>
                                <span className="text-red-600">{beforeValue || 'empty'}</span>
                                <span className="mx-1">" → "</span>
                                <span className="text-green-600">{afterValue || 'empty'}</span>
                                <span>"</span>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                    {log.action === 'update' && log.changes?.fields && log.changes.fields.length === 1 && (
                      <p className="text-xs text-secondary-600 mt-1">
                        Field: {log.changes.fields[0]}
                      </p>
                    )}
                    {log.metadata?.source && (
                      <p className="text-xs text-secondary-500 mt-1">
                        Source: {log.metadata.source}
                      </p>
                    )}
                  </div>
                  <span className="text-xs text-secondary-500 ml-2 flex-shrink-0">
                    {format(new Date(log.created_at), 'MMM d, yyyy HH:mm')}
                  </span>
                </div>
                <div className="flex items-center mt-2">
                  <User className="w-3 h-3 text-secondary-400 mr-1" />
                  <span className="text-xs text-secondary-600">{getUserDisplayName(log)}</span>
                </div>
              </div>
            </div>
          ))}
          
          {/* Pagination Controls */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between pt-3 border-t border-secondary-200">
              <div className="text-xs text-secondary-500">
                Page {currentPage + 1} of {totalPages} ({totalCount} total)
              </div>
              <div className="flex items-center space-x-1">
                <button
                  onClick={() => setCurrentPage(currentPage - 1)}
                  disabled={!hasPrevPage}
                  className="px-2 py-1 text-xs text-secondary-600 bg-white border border-secondary-300 rounded hover:bg-secondary-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                >
                  <ChevronLeft className="w-3 h-3 mr-1" />
                  Previous
                </button>
                <button
                  onClick={() => setCurrentPage(currentPage + 1)}
                  disabled={!hasNextPage}
                  className="px-2 py-1 text-xs text-secondary-600 bg-white border border-secondary-300 rounded hover:bg-secondary-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                >
                  Next
                  <ChevronRight className="w-3 h-3 ml-1" />
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
